import { Trophy } from 'lucide-react';
import type { FC } from 'react';

interface TopSponsor {
  sponsorId: string;
  totalTokens: number;
  validatedReferrals: number;
  pentagonLevel: number;
}

interface TopSponsorsTableProps {
  sponsors?: TopSponsor[];
  loading?: boolean;
}

const LEVEL_STYLES: Record<number, { label: string; classes: string }> = {
  1: {
    label: 'Niv. 1',
    classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  2: {
    label: 'Niv. 2',
    classes: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  },
};

const getLevel = (level: number) => {
  if (level >= 3) {
    return {
      label: `Niv. ${level}`,
      classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    };
  }
  return LEVEL_STYLES[level] ?? { label: `Niv. ${level}`, classes: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
};

const truncateId = (id: string) => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
};

const formatTokens = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

const SkeletonRow: FC = () => (
  <tr className="animate-pulse">
    {[...Array(5)].map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 w-16 rounded bg-slate-200 dark:bg-slate-700" />
      </td>
    ))}
  </tr>
);

const TopSponsorsTable: FC<TopSponsorsTableProps> = ({ sponsors, loading = false }) => {
  const sorted = sponsors ? [...sponsors].sort((a, b) => b.totalTokens - a.totalTokens) : [];

  return (
    <div className="fc-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 px-5 py-4">
        <Trophy className="w-4 h-4 text-amber-500 dark:text-amber-400" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Top Sponsors
        </h3>
        {sorted.length > 0 && (
          <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
            Top {sorted.length}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Rang
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Sponsor ID
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Tokens Gagnés
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Filleuls Validés
              </th>
              <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Niveau Pentagon
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400 dark:text-slate-500">
                  Aucun sponsor trouvé
                </td>
              </tr>
            )}

            {!loading &&
              sorted.map((sponsor, index) => {
                const level = getLevel(sponsor.pentagonLevel);
                const rank = index + 1;

                return (
                  <tr
                    key={sponsor.sponsorId}
                    className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30"
                  >
                    {/* Rang */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                          rank <= 3
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {rank}
                      </span>
                    </td>

                    {/* Sponsor ID */}
                    <td className="px-4 py-3">
                      <span
                        className="font-mono text-xs text-slate-700 dark:text-slate-300"
                        title={sponsor.sponsorId}
                      >
                        {truncateId(sponsor.sponsorId)}
                      </span>
                    </td>

                    {/* Tokens */}
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">
                      {formatTokens(sponsor.totalTokens)}
                    </td>

                    {/* Filleuls validés */}
                    <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">
                      {sponsor.validatedReferrals}
                    </td>

                    {/* Niveau Pentagon */}
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${level.classes}`}
                      >
                        {level.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopSponsorsTable;
